import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import type { Profile, JobListItem, MatchResult, TelegramStatus } from '../../types/api'
import Button from '../../components/ui/Button'
import Badge from '../../components/ui/Badge'
import Modal from '../../components/ui/Modal'
import ScoreBadge from '../../components/ui/ScoreBadge'
import MarkdownViewer from '../../components/ui/MarkdownViewer'
import ProfileChat from '../../components/ui/ProfileChat'

function parseFrontmatter(md: string) {
  const match = md.match(/^---\n([\s\S]*?)\n---/)
  if (!match) return {} as Record<string, string>
  const result: Record<string, string> = {}
  const lines = match[1].split('\n')
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const colon = line.indexOf(':')
    if (colon === -1) { i++; continue }
    const key = line.slice(0, colon).trim()
    const raw = line.slice(colon + 1).trim()
    if (raw === '') {
      const items: string[] = []
      let j = i + 1
      while (j < lines.length && /^\s+-\s/.test(lines[j])) {
        items.push(lines[j].replace(/^\s+-\s*/, '').trim().replace(/^"|"$/g, ''))
        j++
      }
      result[key] = items.join(', ')
      i = j
    } else {
      result[key] = raw.replace(/^"|"$/g, '')
      i++
    }
  }
  return result
}

type EditForm = {
  name: string
  email: string
  phone: string
  telegram: string
  location: string
  skills: string
  linkedin: string
  github: string
}

export default function HrCandidateDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [jobs, setJobs] = useState<JobListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedJob, setSelectedJob] = useState('')
  const [matches, setMatches] = useState<MatchResult[] | null>(null)
  const [matchLoading, setMatchLoading] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [editForm, setEditForm] = useState<EditForm | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [telegramStatus, setTelegramStatus] = useState<TelegramStatus | null>(null)
  const [showMessage, setShowMessage] = useState(false)
  const [messageText, setMessageText] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [messageError, setMessageError] = useState<string | null>(null)
  const [messageSent, setMessageSent] = useState(false)

  const loadProfile = () => {
    if (!id) return
    Promise.all([api.profiles.get(id), api.jobs.list()])
      .then(([p, j]) => {
        setProfile(p.data)
        setJobs(j.data.filter((job) => job.type === 'Listed' || job.type === 'Exclusive'))
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadProfile() }, [id])

  useEffect(() => {
    if (!id || !profile) return
    api.telegram.getStatus(id)
      .then((res) => setTelegramStatus(res.data))
      .catch(() => {})
  }, [id, profile])

  const openEdit = () => {
    if (!profile) return
    const fm = parseFrontmatter(profile.profile)
    setEditForm({
      name: fm.name ?? '',
      email: fm.email ?? '',
      phone: fm.phone ?? '',
      telegram: fm.telegram ?? '',
      location: fm.location ?? '',
      skills: fm.skills ?? '',
      linkedin: fm.linkedin ?? '',
      github: fm.github ?? '',
    })
    setSaveError(null)
    setShowEdit(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !editForm) return
    setSaveError(null)
    setSaving(true)
    try {
      const skills = editForm.skills.split(',').map((s) => s.trim()).filter(Boolean)
      await api.profiles.updateFrontmatter(id, {
        name: editForm.name,
        ...(editForm.email && { email: editForm.email }),
        ...(editForm.phone && { phone: editForm.phone }),
        ...(editForm.telegram && { telegram: editForm.telegram }),
        ...(editForm.location && { location: editForm.location }),
        ...(skills.length && { skills }),
        ...(editForm.linkedin && { linkedin: editForm.linkedin }),
        ...(editForm.github && { github: editForm.github }),
      })
      setShowEdit(false)
      setLoading(true)
      loadProfile()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleCopyLink = () => {
    if (!telegramStatus?.deepLink) return
    navigator.clipboard.writeText(telegramStatus.deepLink)
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !messageText.trim()) return
    setMessageError(null)
    setSendingMessage(true)
    try {
      await api.telegram.sendMessage(id, messageText)
      setMessageSent(true)
      setMessageText('')
      setTimeout(() => { setShowMessage(false); setMessageSent(false) }, 1500)
    } catch (err) {
      setMessageError(err instanceof Error ? err.message : 'Failed to send')
    } finally {
      setSendingMessage(false)
    }
  }

  const runMatch = async () => {
    setMatchLoading(true)
    try {
      const res = await api.jobs.matches.create(selectedJob)
      setMatches(res.data.matches)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Matching failed')
    } finally {
      setMatchLoading(false)
    }
  }

  if (loading) return <p className="text-text-muted text-sm">Loading candidate...</p>
  if (error) return <p className="text-red-400 text-sm">{error}</p>
  if (!profile) return <p className="text-text-muted text-sm">Not found.</p>

  const fm = parseFrontmatter(profile.profile)
  const skillsList = fm.skills ? fm.skills.split(',').map((s) => s.trim()).filter(Boolean) : []

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="text-xs text-text-muted hover:text-primary tracking-tactical"
        >
          ← BACK
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl">{fm.name || 'Unknown'}</h1>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-text-muted">
                {fm.email && <span>{fm.email}</span>}
                {fm.phone && <span>{fm.phone}</span>}
                {fm.telegram && <span>{fm.telegram}</span>}
                {fm.location && <span>{fm.location}</span>}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {fm.linkedin && (
                  <a href={fm.linkedin} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">LinkedIn</a>
                )}
                {fm.github && (
                  <a href={fm.github} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">GitHub</a>
                )}
              </div>
              {telegramStatus && (telegramStatus.connected || telegramStatus.deepLink) && (
                <div className="flex items-center gap-2 mt-3">
                  {telegramStatus.connected ? (
                    <>
                      <Badge variant="preferred">TELEGRAM CONNECTED</Badge>
                      <Button variant="secondary" onClick={() => { setMessageError(null); setShowMessage(true) }}>SEND MESSAGE</Button>
                    </>
                  ) : telegramStatus.deepLink && fm.telegram ? (
                    <>
                      <Badge variant="muted">TELEGRAM NOT CONNECTED</Badge>
                      <Button variant="ghost" onClick={handleCopyLink}>COPY LINK</Button>
                    </>
                  ) : null}
                </div>
              )}
            </div>
            <Button variant="ghost" onClick={openEdit}>EDIT</Button>
          </div>

          {skillsList.length > 0 && (
            <div>
              <p className="text-xs text-text-muted tracking-tactical mb-2">SKILLS</p>
              <div className="flex flex-wrap gap-1">
                {skillsList.map((s) => <Badge key={s}>{s}</Badge>)}
              </div>
            </div>
          )}

          <div className="border-t border-border-subtle pt-4">
            <MarkdownViewer content={profile.profile} />
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-surface-low border border-border-subtle p-4 space-y-3">
            <p className="text-xs text-text-muted tracking-tactical">RUN MATCHING</p>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
            >
              <option value="">Select a job...</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>{j.title} — {j.employer}</option>
              ))}
            </select>
            <Button
              variant="secondary"
              onClick={runMatch}
              disabled={!selectedJob || matchLoading}
              className="w-full justify-center"
            >
              {matchLoading ? 'ANALYZING...' : 'RUN MATCHING'}
            </Button>
          </div>

          {matches && (
            <div className="bg-surface-low border border-border-subtle p-4 space-y-3">
              <p className="text-xs text-text-muted tracking-tactical mb-2">MATCH RESULTS</p>
              {matches.length === 0 ? (
                <p className="text-text-muted text-sm">No candidates found.</p>
              ) : (
                <ul className="space-y-3">
                  {matches.map((m) => (
                    <li key={m.id} className="flex gap-3 items-start">
                      <ScoreBadge score={m.score} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{m.name}</p>
                        <ul className="mt-1 space-y-0.5">
                          {m.reasons.map((r, i) => (
                            <li key={i} className="text-xs text-text-muted">{r}</li>
                          ))}
                        </ul>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div>
            <p className="text-xs text-text-muted tracking-tactical mb-2">PROFILE CHAT</p>
            <ProfileChat profileId={id!} onProfileUpdated={loadProfile} />
          </div>
        </div>
      </div>

      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} title="EDIT CANDIDATE">
        {editForm && (
          <form onSubmit={handleSave} className="space-y-3">
            {saveError && <p className="text-red-400 text-xs">{saveError}</p>}
            {[
              { key: 'name', label: 'NAME *', placeholder: 'John Doe' },
              { key: 'email', label: 'EMAIL', placeholder: 'john@example.com' },
              { key: 'phone', label: 'PHONE', placeholder: '+358 40 123 4567' },
              { key: 'telegram', label: 'TELEGRAM', placeholder: '@johndoe' },
              { key: 'location', label: 'LOCATION', placeholder: 'Helsinki, Finland' },
              { key: 'linkedin', label: 'LINKEDIN', placeholder: 'https://linkedin.com/in/...' },
              { key: 'github', label: 'GITHUB', placeholder: 'https://github.com/...' },
              { key: 'skills', label: 'SKILLS (comma-separated)', placeholder: 'TypeScript, React, Node.js' },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label className="block text-xs text-text-muted tracking-tactical mb-1">{label}</label>
                <input
                  type="text"
                  value={editForm[key as keyof EditForm]}
                  onChange={(e) => setEditForm((f) => f ? { ...f, [key]: e.target.value } : f)}
                  placeholder={placeholder}
                  className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
                />
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving}>{saving ? 'SAVING...' : 'SAVE CHANGES'}</Button>
              <Button variant="ghost" type="button" onClick={() => setShowEdit(false)}>CANCEL</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={showMessage} onClose={() => setShowMessage(false)} title="SEND MESSAGE">
        <form onSubmit={handleSendMessage} className="space-y-3">
          {messageError && <p className="text-red-400 text-xs">{messageError}</p>}
          {messageSent && <p className="text-primary text-xs">Message sent!</p>}
          <div>
            <label className="block text-xs text-text-muted tracking-tactical mb-1">MESSAGE</label>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message..."
              rows={5}
              className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary resize-none"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={sendingMessage || !messageText.trim()}>
              {sendingMessage ? 'SENDING...' : 'SEND'}
            </Button>
            <Button variant="ghost" type="button" onClick={() => setShowMessage(false)}>CANCEL</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
